import React, { useState, useCallback, memo, useMemo } from 'react';
import { Send, ClipboardEdit } from 'lucide-react';
import FileUpload from './FileUpload';
import FileUploadButton from './FileUploadButton';
import { FileItem } from '../../types';
import { useDebounceCallback } from '../../hooks/useDebounce';

interface InputAreaProps {
    files: FileItem[];
    onSend: (message: string) => Promise<void>;
    onFileSelect: (files: File[]) => void;
    onRemoveFile: (id: number) => void;
    onClearFiles: () => void;
    onManualEntry: () => void;
    prefillSignal?: string | null;   // 会话切换信号（新会话时变化）
    shouldPrefill?: boolean;         // 空会话=true → 预填默认提问
}

// 2026-09-17 友好性①：预填默认提问（与离线版 DEFAULT_MSG 逐字一致）
const DEFAULT_MSG = '是NTRK融合吗？符合TL118|维泰凯-拉罗|罗圣全-恩曲的基因要求吗？';

const InputArea: React.FC<InputAreaProps> = memo(({
    files,
    onSend,
    onFileSelect,
    onRemoveFile,
    onClearFiles,
    onManualEntry,
    prefillSignal,
    shouldPrefill
}) => {
    const [inputText, setInputText] = useState('');

    // ① 预填：进门/新会话且会话为空时预填默认提问（对齐离线版 resetInput(true)）
    React.useEffect(() => {
        if (shouldPrefill) setInputText(DEFAULT_MSG);
    }, [prefillSignal, shouldPrefill]);
    const [isComposing, setIsComposing] = useState(false);
    const [isSending, setIsSending] = useState(false);

    // 防抖的自动保存功能（可选）
    const debouncedSave = useDebounceCallback((text: string) => {
        // 可以在这里实现草稿保存功能
        localStorage.setItem('draft_message', text);
    }, 1000);

    const handleSend = useCallback(async () => {
        console.log('🚀 InputArea handleSend called:', {
            inputText: inputText.trim(),
            filesLength: files.length,
            isSending: isSending
        });

        if ((inputText.trim() === '' && files.length === 0) || isSending) {
            console.log('❌ Send blocked:', {
                emptyInput: inputText.trim() === '',
                noFiles: files.length === 0,
                isSending: isSending
            });
            return;
        }

        const messageToSend = inputText.trim();
        setInputText('');
        setIsSending(true);

        // 清除草稿
        localStorage.removeItem('draft_message');

        try {
            console.log('📤 Calling onSend with message:', messageToSend);
            await onSend(messageToSend);
            console.log('✅ onSend completed successfully');
        } catch (error) {
            console.error('❌ 发送消息失败:', error);
            setInputText(messageToSend);
        } finally {
            setIsSending(false);
        }
    }, [inputText, files.length, isSending, onSend]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        console.log('🔍 InputArea KeyDown:', {
            key: e.key,
            shiftKey: e.shiftKey,
            isComposing: isComposing,
            inputText: inputText,
            filesLength: files.length,
            isSending: isSending
        });

        if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
            console.log('🎯 Enter key detected - calling handleSend');
            e.preventDefault();
            handleSend();
        }
    }, [isComposing, handleSend, inputText, files.length, isSending]);

    const handleCompositionStart = useCallback(() => {
        setIsComposing(true);
    }, []);

    const handleCompositionEnd = useCallback(() => {
        setIsComposing(false);
    }, []);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setInputText(value);
        debouncedSave(value);
    }, [debouncedSave]);

    // 优化按钮状态
    const isSendDisabled = useMemo(() => {
        return isSending || (inputText.trim() === '' && files.length === 0);
    }, [isSending, inputText, files.length]);

    // 优化占位符文本
    const placeholderText = useMemo(() => {
        return files.length > 0
            ? `已选择 ${files.length} 个文件，输入您的问题... (Shift+Enter换行，Enter发送)`
            : "输入您的问题... (Shift+Enter换行，Enter发送)";
    }, [files.length]);

    return (
        <div className="input-container">
            {/* 文件 chips 挪输入框上面（2026-09-20 用户令：放下面看不见；此前 0918 令放下面，作废） */}
            <FileUpload
                files={files}
                onFileSelect={onFileSelect}
                onRemoveFile={onRemoveFile}
                onClearFiles={onClearFiles}
            />
            <div className="input-wrapper">
                <div className="input-actions-left">
                    <FileUploadButton onFileSelect={onFileSelect} />
                </div>
                <textarea
                    value={inputText}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onCompositionStart={handleCompositionStart}
                    onCompositionEnd={handleCompositionEnd}
                    placeholder={placeholderText}
                    rows={1} /* 2026-09-18 用户令：对齐生产链单行低输入框 */
                    className="message-input"
                    disabled={isSending}
                />
                <div className="input-actions">
                    <button
                        className="manual-entry-button"
                        onClick={onManualEntry}
                        disabled={isSending}
                        title="手动录入变异信息（无需上传报告）"
                        style={{
                            border: 'none', background: 'none', cursor: isSending ? 'not-allowed' : 'pointer',
                            color: '#666', padding: '6px', display: 'flex', alignItems: 'center',
                            opacity: isSending ? 0.5 : 1,
                        }}
                    >
                        <ClipboardEdit size={20} />
                    </button>
                    <button
                        className="send-button"
                        onClick={handleSend}
                        disabled={isSendDisabled}
                        title={isSendDisabled ? "请输入消息或选择文件" : "发送消息"}
                    >
                        <Send size={20} />
                    </button>
                </div>
            </div>

        </div>
    );
});

InputArea.displayName = 'InputArea';

export default InputArea;